import { IconButton, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { lookupFavourites, toggleFavourite } from './api';

/**
 * Heart button on song cards + song detail page.
 *
 *  - Anonymous user: click → redirect to /login?next=<current url>
 *  - Authenticated: optimistic toggle, roll back on error
 *  - `initialFavourited` may come from a parent bulk lookup; when omitted the
 *    component fetches its own state on mount (only for authenticated users)
 */
export function FavouriteButton({
  songId,
  size = 'sm',
  initialFavourited,
}: {
  songId?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  initialFavourited?: boolean;
}) {
  const { authenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [favourited, setFavourited] = useState<boolean>(!!initialFavourited);
  const [pending, setPending] = useState(false);
  const [loaded, setLoaded] = useState<boolean>(initialFavourited !== undefined);

  useEffect(() => {
    let cancelled = false;
    if (!authenticated || !songId || loaded) return;
    lookupFavourites([songId])
      .then((map) => {
        if (!cancelled) {
          setFavourited(!!map[songId]);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated, songId, loaded]);

  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!songId) return;
      if (!authenticated) {
        const next = `${location.pathname}${location.search}`;
        navigate(`/login?next=${encodeURIComponent(next)}`);
        return;
      }
      const prev = favourited;
      setFavourited(!prev);
      setPending(true);
      try {
        const res = await toggleFavourite(songId);
        setFavourited(res.favourited);
      } catch (err: any) {
        setFavourited(prev);
        toast({
          title: err?.message || 'Could not update favourite',
          status: 'error',
          duration: 2500,
          isClosable: true,
        });
      } finally {
        setPending(false);
      }
    },
    [authenticated, favourited, location.pathname, location.search, navigate, songId, toast],
  );

  return (
    <IconButton
      aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
      icon={<HeartIcon filled={favourited} />}
      size={size}
      variant="ghost"
      color={favourited ? 'pink.400' : 'whiteAlpha.700'}
      _hover={{ color: 'pink.300', bg: 'whiteAlpha.200' }}
      onClick={onClick}
      isDisabled={pending || !songId}
      data-cta="favourites:toggle"
    />
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ display: 'block' }}>
      <path
        d="M12 21s-7.5-5.03-9.6-9.36C1.09 8.7 2.66 5 6.11 5c1.85 0 3.4 1.02 4.19 2.5C11.13 6.02 12.71 5 14.5 5c3.44 0 5.02 3.7 3.71 6.64C19.5 15.97 12 21 12 21z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}
